import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { ComponentCanDeactivate } from 'app/helpers/pendingChangesGuard';
import { ExposedReadGroupRes } from 'app/interfaces/requests/Exposed/exposedReadGroupRes';
import { ListGroupEntriesRes } from 'app/interfaces/requests/List/listGroupEntriesRes';
import { ExposedListPublicRegistryCompaniesRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryCompaniesRes';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListGroupTypesRes } from 'app/interfaces/responses/List/listGroupTypesRes';
import { ListGroupsRes } from 'app/interfaces/responses/List/listGroupsRes';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { GroupsService } from 'app/services/api/groups/groups.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { GROUPS } from 'common/constants/groups.constants';
import { USER_ROLES } from 'common/constants/user-role.constants';
import latinize from 'latinize';
import {
    Observable,
    Subject,
    Subscription,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    forkJoin,
    of,
    switchMap,
    take,
} from 'rxjs';

@Component({
    selector: 'app-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss'],
})
export class GroupsComponent implements ComponentCanDeactivate, AfterViewInit, OnInit, OnDestroy {
    //#region Globals

    // Makes sure the user cannot refresh page without being asked for confirmation.
    beforeLeaveMessage = 'Þú hefur sett inn ný gögn. Ertu viss um að þú viljir fara af síðu án þess að vista?';
    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        try {
            let hasNoFormChanges = (this.stateAddGroup || this.stateEditGroup || this.stateViewOnly) && this.form.dirty;
            return !hasNoFormChanges;
        } catch (e) {
            return true;
        }
    }

    @ViewChild('tablePaginator') tablePaginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    tableColumns: string[] = ['ssn', 'action'];
    totalEntries: number = 0;
    pageSize: number = 25;
    pageIndex: number = 0;
    filterValue: string = '';
    userRoles = USER_ROLES;

    // Form
    form: UntypedFormGroup;
    filterControl = new UntypedFormControl();
    formEnabled = true;
    submitted = false;

    // Data
    dataSource = new MatTableDataSource<UntypedFormGroup>([]);
    groups: Array<ListGroupsRes> = [];
    groupTypes: Array<ListGroupTypesRes>;

    // Search for group
    searchInputControl: UntypedFormControl = new UntypedFormControl('');
    searchInput: string = '';
    groupsCount: number = 0;
    searchedGroups: Array<ListGroupsRes> = [];

    // Alerts
    duplicates: string[] = [];
    newMembers: string[] = [];
    invalidSsns: string[] = [];
    newMemberEvents$ = new Subject<void>();
    duplicateEvents$ = new Subject<void>();
    subs = new Subscription();

    // If selectedGroupId takes the value -1 then we are adding a new group.
    selectedGroupId: number | null;
    stateAddGroup = false;
    stateEditGroup = false;
    stateViewOnly = false;
    stateListGroups = false;

    userDetails: ExposedReadUserDetailsRes = null;

    groupFirms = false;
    groupPersons = false;
    groupMixed = false;
    canEdit = false;
    invalid: AbstractControl<any, any>[];
    groupCount: number = 0;

    //#endregion

    //#region Init

    constructor(
        public lsrauthService: LsrauthService,
        public configsService: ConfigsService,
        public translationService: TranslationService,
        public groupsService: GroupsService,
        public dialog: MatDialog,
        private formBuilder: UntypedFormBuilder,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertsService: AlertsService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.fetchGroupTypes();
        this.bootstrap();
        if (!this.stateViewOnly) {
            this.filterControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((f) => {
                this.filterValue = f.trim().toLowerCase();
                this.tablePaginator.firstPage();
                this.loadEntriesPage(0);
            });
        }

        this.subs.add(this.newMemberEvents$.pipe(debounceTime(300)).subscribe(() => this.showNewMemberAlert()));
        this.subs.add(this.duplicateEvents$.pipe(debounceTime(300)).subscribe(() => this.showCombinedAlert()));
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.tablePaginator;
        this.dataSource.sort = this.sort;
    }

    ngOnDestroy() {
        // Clean up both subscriptions and pending debounces
        this.subs.unsubscribe();
    }

    //#endregion

    //#region Helper / General functions

    private bootstrap() {
        combineLatest([
            this.lsrauthService.userDetails.pipe(
                filter((u) => !!u),
                take(1)
            ),
            this._activatedRoute.paramMap.pipe(take(1)),
        ])
            .pipe(
                switchMap(([user, params]) => {
                    this.userDetails = user;
                    this.selectedGroupId = Number(params.get('selectedGroupId')) || 0;
                    if (this.selectedGroupId > 0) {
                        // Fire both calls in parallel
                        return forkJoin({
                            metadata: this.groupsService.readGroupMetadata({
                                groupId: this.selectedGroupId,
                            }),
                            firstPage: this.groupsService.readGroupEntries({
                                groupId: this.selectedGroupId,
                                pageIndex: 0,
                                pageSize: this.pageSize,
                                filter: '',
                            }),
                        });
                    } else if (this.selectedGroupId == -1) {
                        this.stateAddGroup = true;
                        this.initForm();
                    } else {
                        this.stateListGroups = true;
                        this.fetchGroups();
                    }
                    return of(null);
                })
            )
            .subscribe((result) => {
                if (!result) return;

                const isOwner = result.metadata.groupEntryOwnerUserId === this.userDetails.user.userId;
                const canEditSvid = !!result.metadata.svid && this.lsrauthService.inRole(this.userDetails, USER_ROLES.EDIT_SVID_GROUPS);
                this.canEdit = isOwner || canEditSvid;
                this.stateEditGroup = this.canEdit;
                this.stateViewOnly = !this.canEdit;
                this.groupCount = result.firstPage.totalCount;
                this.totalEntries = result.firstPage.totalCount;

                if (this.stateViewOnly) {
                    this.initFormMetadata(result.metadata);
                    this.dataSource.data = result.firstPage.items.map(this.toFormGroup);
                } else {
                    this.fetchGroupTypes();
                    this.initForm(this.selectedGroupId);
                }
            });
    }

    toFormGroup = (e: ListGroupEntriesRes) =>
        this.formBuilder.group({
            ssn: [formatKennitala(e.ssn)],
            name: [e.name],
            entryType: [e.entryType],
        });

    checkGroupType() {
        if (!this.form || !this.form.get('groupTypeId')) {
            return;
        }

        let groupTypeId = this.form.get('groupTypeId')?.value;
        if (!this.groupTypes || !groupTypeId) {
            return;
        }

        this.groupFirms = false;
        this.groupPersons = false;
        this.groupMixed = false;
        if (this.groupTypes != null) {
            if (this.groupTypes.find((x) => x.groupTypeId == groupTypeId).name == 'label_group_persons') {
                this.groupPersons = true;
            } else if (this.groupTypes.find((x) => x.groupTypeId == groupTypeId).name == 'label_group_firms') {
                this.groupFirms = true;
            } else {
                this.groupMixed = true;
            }
        }

        const allowed = this.groupPersons ? [GROUPS.PERSON] : this.groupFirms ? [GROUPS.FIRM] : [GROUPS.PERSON, GROUPS.FIRM];
        const entries = this.form.get('ssnEntries') as FormArray;
        this.invalid = entries.controls.filter((ctrl) => !allowed.includes(ctrl.get('entryType')?.value));

        if (this.invalid.length) {
            entries.setErrors({ typeMismatch: true });
        } else {
            entries.setErrors(null);
        }
    }

    findGroupTypeName(group: ListGroupsRes) {
        return this.groupTypes.find((x) => x.groupTypeId == group.groupTypeId).name;
    }

    formatKennitala(kt) {
        if (kt) {
            return formatKennitala(kt);
        } else {
            return '';
        }
    }

    onFilterChanged(value: string) {
        this.filterValue = value.trim().toLowerCase();
        this.tablePaginator.firstPage();
        this.loadEntriesPage(0);
    }

    onPageChanged(event: PageEvent) {
        if (!this.stateViewOnly) return;
        this.pageSize = event.pageSize;
        this.loadEntriesPage(event.pageIndex);
    }

    loadEntriesPage(pageIndex: number) {
        this.groupsService
            .readGroupEntries({
                groupId: this.selectedGroupId,
                pageIndex: pageIndex,
                pageSize: this.pageSize,
                filter: this.filterValue,
            })
            .subscribe((res) => {
                // Turn each entry into a FormGroup so the table rows still bind to formGroup
                this.dataSource.data = res.items.map((e) =>
                    this.formBuilder.group({
                        ssn: [formatKennitala(e.ssn)],
                        name: [e.name],
                        entryType: [e.entryType],
                    })
                );
                this.totalEntries = res.totalCount;
            });
    }

    sortGroups(a: ListGroupsRes, b: ListGroupsRes) {
        if (latinize(a.name) < latinize(b.name)) {
            return -1;
        }
        if (latinize(a.name) > latinize(b.name)) {
            return 1;
        }
        return 0;
    }

    cancelGroup() {
        this._router.navigate(['/hopar']);
    }

    searchGroup(term: string) {
        let searchName = this.groups.filter((x) => x.name.toLowerCase().includes(term.toLowerCase()));
        let searchDescription = this.groups.filter((x) => x.description && x.description.includes(term.toLocaleLowerCase()));
        let searchSvid = this.groups.filter((x) => x.svid && x.svid.includes(term.toLocaleLowerCase()));
        let searchCombined = searchName
            .concat(searchDescription)
            .concat(searchSvid)
            .filter(function (elem, index, self) {
                return index === self.indexOf(elem);
            });
        this.searchedGroups = searchCombined.sort(this.sortGroups);
        this.groupsCount = searchCombined.length;
    }

    searchPeople(filterValue: string) {
        const normalized = filterValue.trim().toLocaleLowerCase();
        this.dataSource.filter = normalized;
    }

    private showNewMemberAlert() {
        const unique = Array.from(new Set(this.newMembers));
        if (unique.length > 0) {
            this._alertsService.changeAlertInfoHeader(unique.length == 1 ? 'Meðlimi bætt við í hópinn' : 'Meðlimum bætt við í hópinn');
            this._alertsService.changeAlertInfoMessage(`${unique.length} nýjum bætt við í hópinn`);
            this._alertsService.changeAlertInfoShow(true);
        }
        // reset
        this.newMembers = [];
    }

    private showCombinedAlert() {
        const dialogMessages: string[] = [];

        if (this.duplicates.length) {
            const u = Array.from(new Set(this.duplicates));
            let msg =
                u.length === 1
                    ? `Eftirfarandi kennitala ${u[0]} er þegar í hópi.`
                    : u.length > 10
                      ? `${u.length} kennitölur eru þegar í hópi.`
                      : `Eftirfarandi kennitölur eru þegar í hópi: ${u.join(', ')}.`;
            dialogMessages.push(msg);
        }

        if (this.invalidSsns.length) {
            dialogMessages.push('Kennitölurnar sem eftir standa í innsláttarreitnum fundust ekki á skrá.');
        }
        const fullMessage = dialogMessages.join('&#10; &#10;');
        let confirmationForm = this.formBuilder.group({
            title: this.translationService.translate('label_attention'),
            message: fullMessage,
            icon: {
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        this._fuseConfirmationService.open(confirmationForm.value);
        // reset
        this.invalidSsns = [];
        this.duplicates = [];
    }

    handleInvalidSsns(invalid: string[]) {
        this.invalidSsns.push(...invalid);
        this.duplicateEvents$.next();
    }

    canEditGroup(group: ListGroupsRes) {
        return (
            group.groupEntryOwnerUserId === this.userDetails.user.userId ||
            (group.svid && this.lsrauthService.inRole(this.userDetails, USER_ROLES.EDIT_SVID_GROUPS))
        );
    }

    //#endregion

    //#region Add/Edit/Delete group

    addGroup() {
        this._router.navigate(['/hopar/-1']);
        this.selectedGroupId = -1;
    }

    editGroup(group) {
        this._router.navigate(['/hopar/' + group.groupId]);
        this.selectedGroupId = group.groupId;
        this.initForm(group.groupId);
    }

    viewGroup(group) {
        this._router.navigate(['/hopar/' + group.groupId]);
        this.selectedGroupId = group.groupId;
        this.initFormMetadata(group);
    }

    deleteGroup(groupId) {
        let confirmationForm = this.formBuilder.group({
            title: this.translationService.translate('info_confirm_delete_group_header'),
            message: this.translationService.translate('info_confirm_delete_group_message'),
            icon: {
                show: true,
                name: 'heroicons_solid:trash',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.groupsService.deleteGroup(true, { groupId: groupId }).subscribe(() => {
                    this.fetchGroups();
                });
            }
        });
    }

    //#endregion

    //#region Init form items

    private initFormMetadata(group?: ExposedReadGroupRes) {
        // Builds only the form metadata controls
        this.form = this.formBuilder.group({
            name: [group?.name || '', [Validators.required]],
            description: [group?.description || ''],
            groupTypeId: [group?.groupTypeId || '', [Validators.required]],
            svid: [group?.svid ? true : false],
        });

        this.form.addControl('ssnEntries', this.formBuilder.array([]));
        this.form.get('groupTypeId')!.valueChanges.subscribe(() => this.checkGroupType());
        this.checkGroupType();
    }

    initForm(groupId = null) {
        const canEditSvid = this.lsrauthService.inRole(this.userDetails, USER_ROLES.EDIT_SVID_GROUPS);
        this.submitted = false;

        this.form = this.formBuilder.group({
            ssnSearchInput: new UntypedFormControl(''),
            name: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl(),
            groupTypeId: new UntypedFormControl('', [Validators.required]),
            svid: new UntypedFormControl({
                value: false,
                disabled: this.stateViewOnly || !canEditSvid,
            }),
            ssnEntries: this.formBuilder.array([]),
        });
        this.form.get('groupTypeId')!.valueChanges.subscribe(() => this.checkGroupType());

        this.initDataSource();
        this.checkGroupType();

        if (groupId != null) {
            this.groupsService.readGroup({ groupId: groupId }).subscribe((group) => {
                this.form = this.formBuilder.group({
                    ssnSearchInput: new UntypedFormControl(''),
                    name: new UntypedFormControl(group.name, [Validators.required]),
                    description: new UntypedFormControl(group.description),
                    groupTypeId: new UntypedFormControl(group.groupTypeId, [Validators.required]),
                    svid: new UntypedFormControl(group.svid ? true : false),
                    ssnEntries: this.formBuilder.array([]),
                });
                group.groupEntries.forEach((entry) => {
                    let entries = this.form.get('ssnEntries') as FormArray;
                    entries.push(
                        this.formBuilder.group({
                            ssn: new UntypedFormControl(formatKennitala(entry.ssn)),
                            name: new UntypedFormControl(entry.name),
                            entryType: new UntypedFormControl(entry.entryType),
                        })
                    );
                });
                this.initDataSource();
                this.checkGroupType();
            });
        }
    }

    initDataSource() {
        let entries = this.form.get('ssnEntries') as FormArray;
        this.dataSource = new MatTableDataSource<UntypedFormGroup>(entries.controls as UntypedFormGroup[]);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.tablePaginator;
        this.dataSource.filterPredicate = (group: UntypedFormGroup, filter: string) => {
            const name = (group.get('name')?.value ?? '').toString().toLowerCase();
            const ssn = (group.get('ssn')?.value ?? '').toString().toLowerCase();

            return name.includes(filter) || ssn.includes(filter);
        };
    }

    //#endregion

    //#region Add/Remove a certain person/firm from a group

    entrySelected(entry: ExposedListPublicRegistryPersonsRes | ExposedListPublicRegistryCompaniesRes) {
        if (!entry) return;

        let entries = this.form.get('ssnEntries') as FormArray;
        let ssn: string, name: string, entryType: string;

        if ('ssn' in entry) {
            ssn = formatKennitala(entry.ssn);
            name = entry.name;
            entryType = 'person';
        } else {
            ssn = formatKennitala(entry.idNumber);
            name = entry.name;
            entryType = 'firm';
        }

        if (entries.controls.find((x) => x.get('ssn').value == ssn)) {
            this.duplicates.push(ssn);
            this.duplicateEvents$.next();
        } else {
            entries.push(
                this.formBuilder.group({
                    ssn: new UntypedFormControl(ssn),
                    name: new UntypedFormControl(name),
                    entryType: new UntypedFormControl(entryType),
                })
            );
            this.totalEntries = entries.length;
            this.newMembers.push(ssn);
            this.newMemberEvents$.next();

            this.initDataSource();
            this.checkGroupType();
        }
    }

    deleteSSNEntryFormItem(group: FormGroup) {
        let entries = this.form.get('ssnEntries') as FormArray;
        const i = entries.controls.findIndex((ctrl) => ctrl === group);
        entries.removeAt(i);
        const temp = this.dataSource.filter;
        this.totalEntries = entries.length;
        this.initDataSource();
        this.dataSource.filter = temp;
        this.checkGroupType();
    }

    //#endregion

    //#region HTTP

    fetchGroups() {
        this.subs.add(
            this.searchInputControl.valueChanges.pipe(debounceTime(150), distinctUntilChanged()).subscribe((term: string) => {
                this.searchGroup(term ?? '');
            })
        );

        this.groupsService.listUserGroups().subscribe((res) => {
            this.groups = res;
            this.searchGroup(this.searchInputControl.value ?? '');
        });
    }

    fetchGroupTypes() {
        this.groupsService.listGroupTypes(true).subscribe((res) => {
            this.groupTypes = res;
            this.checkGroupType();
        });
    }

    save() {
        if (!this.form.valid) {
            return;
        }

        this.form.disable();
        this.formEnabled = false;

        let ssnEntries: string[] = [];
        let entries = this.form.get('ssnEntries') as FormArray;
        entries.controls.forEach((entry) => {
            ssnEntries.push(entry.get('ssn').value.replace('-', ''));
        });

        if (this.stateAddGroup) {
            this.groupsService
                .addGroup(true, {
                    Description: this.form.get('description').value,
                    GroupTypeId: this.form.get('groupTypeId').value,
                    Name: this.form.get('name').value,
                    SsnEntries: ssnEntries,
                    SvidGroup: this.form.get('svid').value,
                })
                .subscribe((groupId) => {
                    this.form.enable();
                    this.form.markAsPristine();
                    this.formEnabled = true;
                    if (groupId > 0) {
                        this._router.navigate(['/hopar']);
                    }
                });
        } else if (this.stateEditGroup) {
            this.groupsService
                .editGroup(true, {
                    Description: this.form.get('description').value,
                    GroupTypeId: this.form.get('groupTypeId').value,
                    Name: this.form.get('name').value,
                    SsnEntries: ssnEntries,
                    SvidGroup: this.form.get('svid').value,
                    GroupId: this.selectedGroupId,
                })
                .subscribe((success) => {
                    const isOwner = this.lsrauthService.inRole(this.userDetails, this.userRoles.EDIT_OWN_GROUPS);
                    const canEditSvid = this.lsrauthService.inRole(this.userDetails, this.userRoles.EDIT_SVID_GROUPS);
                    if (!(canEditSvid || isOwner)) {
                        this._router.navigate(['/hopar']);
                        return;
                    }
                    this.form.enable();
                    this.form.markAsPristine();
                    this.formEnabled = true;
                    if (success) {
                        this._router.navigate(['/hopar']);
                    }
                });
        }
    }

    //#endregion
}
